import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { makeRequest } from "../../../assets/functions_helper";
import { Alert, Box, Snackbar } from "@mui/material";
import { CustomDialog, CustomDialogActions, CustomDialogContent, CustomDialogTitle } from "../../../components/customDialog";
import CreateImage from "./image_components/create_image";
import { CloseButton, SendButton } from "../../../components/buttons";
import ListData from "../../../components/listData";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EditImage from "./image_components/edit_image";
import { motion } from "framer-motion";
import { useSubscriptionUsage } from "../../../context/SubscriptionUsageContext";

// Paleta de colores
const COLORS = {
  PRIMARY: "#2596be",
  PRIMARY_HOVER: "#1a7a9e",
  BACKGROUND: "#ffffff",
  PAPER_BG: "#ffffff",
  TEXT_PRIMARY: "#1E1E1E",
  TEXT_SECONDARY: "#6A6A6A",
  BORDER: "#e0e0e0",
  SHADOW: "rgba(37, 150, 190, 0.1)",
};

const columnsConfig = [
  {
    header: "Imagen",
    dataKey: "image_url",
    type: "image",
    imageWidth: 100,
    imageHeight: 100,
    borderRadius: "100%",
    altText: "Imagen",
  },
];

function ListImages({ restaurantId, initialFilter }) {
  const [isLoaded, setIsLoaded] = useState(false);

  const [images, setImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  
  const { usageInfo, fetchUsage } = useSubscriptionUsage();

  // Filtros y categorías
  const [filterCategories, setFilterCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  // Categoría para nueva imagen
  const [categoryCreate, setCategoryCreate] = useState("");

  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [page, setPage] = useState(1);

  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);

  // Estado para el ítem en edición
  const [editingItem, setEditingItem] = useState(null); // { id, name, image_url }

  const [name, setName] = useState("");
  const [image, setImage] = useState(null);

  // Snackbar unificado
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const navigate = useNavigate();

  // Efecto para filtro inicial
  useEffect(() => {
    if (initialFilter) {
      setSearchTerm(initialFilter);
    }
  }, [initialFilter]);

  // Cargar categorías disponibles
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await makeRequest(
          "GET",
          `menu_manager/categories/${restaurantId}?category_type=background_images_categories`,
          {},
          "application/json",
          localStorage.getItem("token"),
          localStorage.getItem("tokenType"),
          null,
          null,
          () => navigate("/login")
        );

        const formattedCategories = response.map((cat) => ({
          id: cat.name,
          label: cat.id,
        }));
        setCategories(formattedCategories);
      } catch (error) {
        console.error("Error al cargar categorías:", error);
      }
    };

    fetchCategories();
  }, [restaurantId, navigate]);

  // Cargar imágenes
  const fetchImages = useCallback(() => {
    makeRequest(
      "GET",
      `menu_manager/list_restaurant_background_images?restaurant_id=${restaurantId}&page=${page}&size=200`,
      {},
      "application/json",
      localStorage.getItem("token"),
      localStorage.getItem("tokenType"),
      null,
      null,
      () => navigate("/login")
    )
      .then((response) => {
        const updatedImages = response.background_images.map((img) => ({
          id: img.id,
          title: img.image_name || "Sin nombre",
          image_url: img.image_url,
          originalData: img,
        }));

        setImages(updatedImages);
        setFilteredImages(updatedImages);

        const uniqueCategories = [
          ...new Set(
            response.background_images
              .map((img) => img.category)
              .filter((cat) => cat && cat.trim() !== "")
          ),
        ].map((cat) => ({ id: cat, title: cat }));

        setFilterCategories([{ id: "", title: "Todas las categorías" }, ...uniqueCategories]);

        setTotal(response.total);
        setPages(response.pages);
        setCurrentPage(response.currentPage);
        setIsLoaded(true);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [restaurantId, page, navigate]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  // Filtrado
  useEffect(() => {
    let result = [...images];

    if (searchTerm.trim() !== "") {
      const lowerTerm = searchTerm.toLowerCase();
      result = result.filter((p) => (p.title || "").toString().toLowerCase().includes(lowerTerm));
    }

    if (selectedCategory && selectedCategory !== "") {
      result = result.filter((img) => img.originalData?.category === selectedCategory);
    }

    setFilteredImages(result);
  }, [images, searchTerm, selectedCategory]);

  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const handleFilterChange = (e) => setSelectedCategory(e.target ? e.target.value : e);

  const refreshStates = () => {
    setName("");
    setImage(null);
    setCategoryCreate("");
    setEditingItem(null);
  };

  // Crear imagen
  const handleSubmitCreateImage = () => {
    if (!name || !image || !categoryCreate) {
      setSnackbar({
        open: true,
        message: "Por favor completa todos los campos obligatorios (nombre, imagen y categoría).",
        severity: "warning",
      });
      return;
    }

    const formData = new FormData();
    formData.append("background_image", image);
    formData.append("restaurant_id", restaurantId);
    formData.append("image_name", name);
    formData.append("category", categoryCreate);

    makeRequest(
      "POST",
      `menu_manager/create_background_image/`,
      formData,
      "multipart/form-data",
      localStorage.getItem("token"),
      localStorage.getItem("tokenType"),
      null,
      null,
      () => navigate("/login")
    )
      .then(() => {
        setOpenCreate(false);
        setSnackbar({ open: true, message: "Imagen creada correctamente", severity: "success" });
        fetchImages();
        fetchUsage(restaurantId);
        refreshStates();
      })
      .catch(() => {
        setSnackbar({ open: true, message: "Ocurrió un error al crear la imagen", severity: "error" });
      });
  };

  // Abrir edición
  const handleOpenEdit = (id, item) => {
    setEditingItem({ id, name: item.title, image_url: item.image_url });
    setName(item.title);         // nombre actual
    setImage(null);              // asegurar que no hay imagen nueva seleccionada
    setOpenEdit(true);
  };

  // Cerrar edición
  const handleCloseEdit = () => {
    setOpenEdit(false);
    refreshStates();
  };

  // Mejorar con IA
  const handleEnhanceImage = (id) => {
    setSnackbar({
      open: true,
      message: "✨ Mejorando imagen con IA... Por favor espera.",
      severity: "info",
    });

    makeRequest(
      "PUT",
      `menu_manager/enhance_image?background_image_id=${id}`,
      {},
      "application/json",
      localStorage.getItem("token"),
      localStorage.getItem("tokenType"),
      null,
      null,
      () => navigate("/login")
    )
      .then(() => {
        fetchImages();
        fetchUsage(restaurantId);
      })
      .catch((error) => {
        console.error(error);
        setSnackbar({ open: true, message: "❌ Error al mejorar la imagen.", severity: "error" });
      });
  };

  const extraActions = [
    {
      icon: <AutoAwesomeIcon fontSize="small" />,
      onClick: (item) => handleEnhanceImage(item.id),
      show: (item) => !item.originalData?.ia_enhanced,
      disabled: (item) => usageInfo?.hasSubscription && usageInfo?.ai_prompts?.remaining <= 0,
      tooltip: "Mejorar imagen con IA",
      disabledTooltip: "Límite de mejoras con IA alcanzado. Mejora tu plan.",
    },
  ];

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      {isLoaded ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
          <ListData
            title="Imágenes"
            data={filteredImages}
            setData={setImages}
            pages={pages}
            total={total}
            search={true}
            searchValue={searchTerm}
            searchFunction={handleSearchChange}
            searchPlaceholder="Buscar imagen..."
            filterSelect={true}
            valuesFilter={filterCategories}
            valueFilter={selectedCategory}
            filterSelectFunction={handleFilterChange}
            edit={true}
            editMessage=""
            editFunction={(id, title, public_id, item)=>{
              handleOpenEdit(id, item)
            }}
            currentPage={currentPage}
            page={page}
            setPage={setPage}
            changePage={() => { }}
            create={true}
            createMessage="Crear Imagen"
            createFunction={() => setOpenCreate(true)}
            createLimitMessage={
              usageInfo?.hasSubscription && usageInfo?.images?.remaining <= 0
                ? "Límite de imágenes alcanzado"
                : ""
            }
            nameColumn1="Nombre"
            nameColumn2={null}
            nameColumn3="Acciones"
            dynamicColumns={columnsConfig}
            extraActions={extraActions}
          />

          {/* Modal Crear Imagen */}
          <CustomDialog open={openCreate} onClose={() => setOpenCreate(false)}>
            <CustomDialogTitle>Crear Imagen</CustomDialogTitle>
            <CustomDialogContent>
              <CreateImage
                name={name}
                setName={setName}
                setImage={setImage}
                categories={categories}
                categoryCreate={categoryCreate}
                setCategoryCreate={setCategoryCreate}
              />
            </CustomDialogContent>
            <CustomDialogActions>
              <CloseButton onClick={() => setOpenCreate(false)}>Cerrar</CloseButton>
              <SendButton onClick={handleSubmitCreateImage}>Enviar</SendButton>
            </CustomDialogActions>
          </CustomDialog>

          {/* Modal Editar Imagen */}
          {editingItem && (
            <EditImage
              key={editingItem.id}           // <-- key forzada para remontar
              open={openEdit}
              handleClose={handleCloseEdit}
              imageId={editingItem.id}
              restaurantId={restaurantId}
              fetchImages={fetchImages}
              currentName={editingItem.name}
              currentImageUrl={editingItem.image_url}
            />
          )}

          {/* Snackbar unificado */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity={snackbar.severity}
              variant="filled"
              sx={{
                width: "100%",
                fontWeight: 500,
                boxShadow: `0 4px 12px ${COLORS.SHADOW}`,
                backgroundColor: snackbar.severity === "success" ? COLORS.PRIMARY : undefined,
              }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </motion.div>
      ) : (
        <Box sx={{ height: "100vh" }} />
      )}
    </>
  );
}

export default ListImages;